import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import {
    DownloadFileInputDto,
    DownloadFilesInputDto,
    DownloadOutputDto,
    DownloadOutputDtoIHandleError,
    DownloadZipInputDto,
    OneDriveServiceProxy
} from '@shared/service-proxies/service-proxies';

export interface DriveDownloadItem {
    keyId: string;
    msDriveId: string;
    name?: string;
    isFolder?: boolean;
}

@Injectable({ providedIn: 'root' })
export class VfDriveDownloadService {

    constructor(private oneDriveService: OneDriveServiceProxy) {}

    downloadFile(item: DriveDownloadItem): Observable<void> {
        return this.downloadFiles([item]);
    }

    downloadFiles(items: DriveDownloadItem[], zipName?: string): Observable<void> {
        const valid = (items || []).filter(i => i && i.keyId && i.msDriveId);
        if (valid.length === 0) {
            return throwError(() => new Error('No valid items to download.'));
        }

        const input = new DownloadFilesInputDto({
            files: valid.map(i => new DownloadFileInputDto({ keyId: i.keyId, msDriveId: i.msDriveId })),
            zipName: zipName
        });

        return this.oneDriveService.downloadFile(input).pipe(
            mergeMap(result => this._saveResponse(result))
        );
    }

    downloadFolder(folder: DriveDownloadItem): Observable<void> {
        if (!folder || !folder.keyId || !folder.msDriveId) {
            return throwError(() => new Error('Invalid folder reference.'));
        }

        const input = new DownloadZipInputDto({
            keyId: folder.keyId,
            msDriveId: folder.msDriveId,
            zipName: folder.name || undefined
        });

        return this.oneDriveService.downloadFolderZip(input).pipe(
            mergeMap(result => this._saveResponse(result))
        );
    }

    /**
     * Routes by item count and shape:
     *   - Single folder  -> folder ZIP endpoint
     *   - Single file    -> downloadFile endpoint with one item
     *   - Multi-select   -> downloadFile endpoint with the full list (files and/or folders);
     *                       backend packages them into a single ZIP.
     */
    downloadItems(items: DriveDownloadItem[], zipName?: string): Observable<void> {
        const valid = (items || []).filter(i => i && i.keyId && i.msDriveId);
        if (valid.length === 0) {
            return throwError(() => new Error('No valid items to download.'));
        }
        if (valid.length === 1 && valid[0].isFolder) {
            return this.downloadFolder(valid[0]);
        }
        return this.downloadFiles(valid, zipName);
    }

    private _saveResponse(result: DownloadOutputDtoIHandleError | null): Observable<void> {
        if (!result) {
            return throwError(() => new Error('Empty download response.'));
        }
        if (result.isError) {
            return throwError(() => new Error(result.errorMessage || 'Download failed.'));
        }
        const data: DownloadOutputDto | undefined = result.responseData;
        // fileBytes === '' is a valid zero-byte file (e.g. blank docs created in-app);
        // only treat null/undefined as a missing payload.
        if (!data || data.fileBytes == null || !data.fileName) {
            return throwError(() => new Error('Download payload missing.'));
        }
        try {
            const blob = this._base64ToBlob(data.fileBytes);
            saveAs(blob, data.fileName);
        } catch (e) {
            return throwError(() => e);
        }
        return of(undefined);
    }

    private _base64ToBlob(base64: string): Blob {
        const cleaned = (base64 || '').replace(/^data:[^;]+;base64,/, '');
        const binary = atob(cleaned);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: 'application/octet-stream' });
    }
}
